// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";

import {
  mdiArrowLeftBold,
  mdiArrowRightBold,
  mdiFilter,
  mdiRefresh,
  mdiStar
} from "@mdi/js";
import { Button, Divider, Input, Slider, Switch, Typography } from "antd";
import { IS_INTERNAL_ENV } from "constants/app.constants";
import moment from "moment";
import styled from "styled-components";

import ContextMenu from "components/charts/toggles/ContextMenu";

import { EntityKind } from "../../models/entityKind";
import type { RootState } from "../../store/rootReducer";
import { DraggableLegendBase } from "../base/DraggableLegendBase";
import { IconButton } from "../pad-generator/ScenarioConfigurationTable";
import { useFacilityHighlightAdapter } from "./hooks/adapters/useFacilityHighlightAdapter";

// ── Constants ────────────────────────────────────────────────────────────────

const { Text, Title } = Typography;
const NETWORK_OPTIONS_DIMENSIONS = { width: 280, height: 235 };

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Thin UI shell for facility highlighting on the map.
 *
 * All imperative Mapbox interactions (source/layer lifecycle, event wiring,
 * data fetching, spiderify hover, contribution highlighting, network map
 * rendering) are encapsulated in {@link useFacilityHighlightAdapter} and
 * its sub-hooks. This component is responsible only for rendering:
 *
 * - Contribution legend (DraggableLegendBase)
 * - Context menu (facility/well right-click actions)
 * - Network map options panel (DraggableLegendBase + NetworkMapOptions)
 * - Network depth legend (DraggableLegendBase)
 */
export function FacilityHighlighter(): JSX.Element {
  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );

  const {
    // Click handler
    clickedEntity,
    showContextMenu,
    setShowContextMenu,
    contextMenuPosition,

    // Contribution highlight
    legend,
    highlightContributionWells,
    clearLegend,
    filterToContributingWells,
    filterToFacilities,
    filterToAllContributingWellsFromAllFacilities,
    filterToNetworkForAllContributingWells,
    setMidstreamFilterFlags,

    // Local network map
    wellNetworkMapRequest,
    setWellNetworkMapRequest,
    networkLineWidth,
    setNetworkLineWidth,
    showNetworkVisualOptions,
    wellNetworkMap,
    showWellNetworkMap,
    clearNetworkMap,
    networkDepthLegend,

    // Cross-cutting
    clearEverything,
    isMidstreamDataSourceEnabled,
    hasFeature,

    // Container dimensions
    containerRef,
    containerWidth,
    containerHeight,

    // Zustand actions for UI close handlers
    clearHighlightContributingWellsForFacility,
    clearNetworkDepthLegend,
    resetClickedEntity
  } = useFacilityHighlightAdapter();

  // ── Derived values ─────────────────────────────────────────────────

  const parentDimensions =
    containerWidth && containerHeight
      ? { width: containerWidth, height: containerHeight }
      : {
          width: (containerRef.current as Maybe<HTMLDivElement>)?.clientWidth ?? 100,
          height: (containerRef.current as Maybe<HTMLDivElement>)?.clientHeight ?? 100
        };

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Root
      ref={containerRef}
      onContextMenu={(e) => e.preventDefault()}
      data-testid={"facility-highlighter"}>
      {/* ── Contribution Legend ──────────────────────────────────────── */}
      <DraggableLegendBase
        visible={legend.visible}
        closeable
        onClose={() => {
          clearLegend();
          clearEverything();
          resetClickedEntity();
          clearHighlightContributingWellsForFacility();
        }}
        parentDimensions={parentDimensions}
        legendTitle={<Title level={5}>{legend.title}</Title>}>
        {legend.legendItems.map((item) => (
          <div key={item.title} style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: item.color,
                marginRight: "5px"
              }}
            />
            <Text style={{ fontSize: "1.2em" }}>{item.title}</Text>
          </div>
        ))}
      </DraggableLegendBase>

      {/* ── Context Menu ────────────────────────────────────────────── */}
      {activeEntityKinds.includes(EntityKind.Facility) &&
        hasFeature("Midstream Allocation Model") &&
        isMidstreamDataSourceEnabled && (
          <ContextMenu
            className={"map-context-menu"}
            visible={showContextMenu}
            allow={showContextMenu}
            position={contextMenuPosition}
            data-testid={"facility-context-menu"}
            content={
              <ContextMenuContent>
                {clickedEntity?.well ? (
                  <WellContextMenuItems
                    onFilterToFacilities={() => {
                      setMidstreamFilterFlags();
                      filterToFacilities();
                      setShowContextMenu(false);
                    }}
                    onShowWellNetworkMap={() => {
                      setMidstreamFilterFlags();
                      showWellNetworkMap();
                      setShowContextMenu(false);
                    }}
                  />
                ) : (
                  <FacilityContextMenuItems
                    onHighlightContributingWells={() => {
                      highlightContributionWells();
                      setShowContextMenu(false);
                    }}
                    onFilterToFacilityAndWells={() => {
                      setMidstreamFilterFlags();
                      filterToContributingWells();
                      filterToFacilities();
                      setShowContextMenu(false);
                    }}
                    onFilterToAllContributingWells={async () => {
                      await filterToAllContributingWellsFromAllFacilities();
                      setShowContextMenu(false);
                    }}
                    onFilterToNetwork={async () => {
                      await filterToNetworkForAllContributingWells();
                      setShowContextMenu(false);
                    }}
                  />
                )}
              </ContextMenuContent>
            }
          />
        )}

      {/* ── Network Map Options Panel ───────────────────────────────── */}
      {showNetworkVisualOptions && (
        <DraggableLegendBase
          visible={true}
          closeable
          initialWidth={NETWORK_OPTIONS_DIMENSIONS.width}
          initialHeight={NETWORK_OPTIONS_DIMENSIONS.height}
          onClose={() => {
            clearNetworkDepthLegend();
            clearNetworkMap();
            resetClickedEntity();
          }}
          parentDimensions={parentDimensions}
          legendTitle={<Title level={5}>Network Options</Title>}>
          <NetworkMapOptions
            onClose={clearNetworkMap}
            showOnlyPrimaryPath={wellNetworkMapRequest.onlyPrimaryPath}
            width={[networkLineWidth[0], networkLineWidth[1]]}
            wellIds={wellNetworkMapRequest.wellIds}
            onChange={(value) => {
              setWellNetworkMapRequest({
                ...wellNetworkMapRequest,
                onlyPrimaryPath: value.showOnlyPrimaryPath,
                date: value.date,
                hideConnectionsLessThanPercent: value.hideConnectionsLessThanPercent
              });
            }}
            date={
              wellNetworkMapRequest.date ??
              (wellNetworkMap as Maybe<{ date?: string }>)?.date
            }
            startDate={(wellNetworkMap as Maybe<{ startDate?: Date }>)?.startDate}
            endDate={(wellNetworkMap as Maybe<{ endDate?: Date }>)?.endDate}
            hideConnectionsLessThanPercent={
              wellNetworkMapRequest.hideConnectionsLessThanPercent
            }
            onWidthChange={(value) => {
              setNetworkLineWidth(value);
            }}
          />
        </DraggableLegendBase>
      )}

      {/* ── Network Depth Legend ─────────────────────────────────────── */}
      {showNetworkVisualOptions && (
        <DraggableLegendBase
          visible={networkDepthLegend.visible}
          closeable
          yOffset={NETWORK_OPTIONS_DIMENSIONS.height + 5}
          onClose={() => {
            clearNetworkDepthLegend();
            clearNetworkMap();
            resetClickedEntity();
          }}
          parentDimensions={parentDimensions}
          legendTitle={<Title level={5}>{networkDepthLegend.title}</Title>}>
          {networkDepthLegend.legendItems.map((item) => (
            <div key={item.title} style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  backgroundColor: item.color,
                  marginRight: "5px"
                }}
              />
              <Text style={{ fontSize: "1.2em" }}>{item.title}</Text>
            </div>
          ))}
        </DraggableLegendBase>
      )}
    </Root>
  );
}

// ── Context Menu Item Components ─────────────────────────────────────────────

interface WellContextMenuItemsProps {
  readonly onFilterToFacilities: () => void;
  readonly onShowWellNetworkMap: () => void;
}

function WellContextMenuItems({
  onFilterToFacilities,
  onShowWellNetworkMap
}: WellContextMenuItemsProps): JSX.Element {
  return (
    <>
      <Button
        type={"text"}
        icon={<Icon path={mdiFilter} size={1.0} />}
        onClick={onFilterToFacilities}
        onContextMenu={(e) => e.preventDefault()}>
        Filter To Sales Facilities
      </Button>
      <Button
        type={"text"}
        icon={<Icon path={mdiFilter} size={1.0} />}
        onClick={onShowWellNetworkMap}
        onContextMenu={(e) => e.preventDefault()}>
        Show Well To Facility Network
      </Button>
    </>
  );
}

interface FacilityContextMenuItemsProps {
  readonly onHighlightContributingWells: () => void;
  readonly onFilterToFacilityAndWells: () => void;
  readonly onFilterToAllContributingWells: () => Promise<void>;
  readonly onFilterToNetwork: () => Promise<void>;
}

function FacilityContextMenuItems({
  onHighlightContributingWells,
  onFilterToFacilityAndWells,
  onFilterToAllContributingWells,
  onFilterToNetwork
}: FacilityContextMenuItemsProps): JSX.Element {
  return (
    <>
      <Button
        type={"text"}
        icon={<Icon path={mdiStar} size={1.0} />}
        onClick={onHighlightContributingWells}
        onContextMenu={(e) => e.preventDefault()}>
        Highlight Contributing Wells
      </Button>
      <Divider />
      <Button
        type={"text"}
        icon={<Icon path={mdiFilter} size={1.0} />}
        onClick={onFilterToFacilityAndWells}
        onContextMenu={(e) => e.preventDefault()}>
        Filter To Facility And Contributing Wells
      </Button>
      <Button
        type={"text"}
        icon={<Icon path={mdiFilter} size={1.0} />}
        onClick={onFilterToAllContributingWells}
        onContextMenu={(e) => e.preventDefault()}>
        Filter To Contributing Wells From All Facilities
      </Button>
      {IS_INTERNAL_ENV && (
        <Button
          type={"text"}
          icon={<Icon path={mdiFilter} size={1.0} />}
          onClick={onFilterToNetwork}
          onContextMenu={(e) => e.preventDefault()}>
          Filter To Network
        </Button>
      )}
    </>
  );
}

// ── NetworkMapOptions ────────────────────────────────────────────────────────

interface NetworkMapSettings {
  showOnlyPrimaryPath?: boolean;
  minFraction?: number;
  date?: string;
  startDate?: Date;
  endDate?: Date;
  width: [number, number];
  onChange?: (settings: NetworkMapSettings) => void;
  onWidthChange?: (width: [number, number]) => void;
  onClose: () => void;
  hideConnectionsLessThanPercent?: number;
  wellIds?: string[];
}

function NetworkMapOptions(options: NetworkMapSettings): JSX.Element {
  const canGoToPreviousMonth = () =>
    options.startDate &&
    options.date &&
    moment(options.date, "YYYY-MM-DD") > moment(options.startDate);

  const canGoToNextMonth = () =>
    options.endDate &&
    options.date &&
    moment(options.date, "YYYY-MM-DD") < moment(options.endDate);

  const goToPreviousMonth = () => {
    if (canGoToPreviousMonth()) {
      const prevMonth = moment(options.date, "YYYY-MM-DD")
        .subtract(1, "months")
        .format("YYYY-MM-DD");
      options.onChange?.({
        ...options,
        date: prevMonth
      });
    }
  };

  const goToNextMonth = () => {
    if (canGoToNextMonth()) {
      const nextMonth = moment(options.date, "YYYY-MM-DD")
        .add(1, "months")
        .format("YYYY-MM-DD");
      options.onChange?.({
        ...options,
        date: nextMonth
      });
    }
  };

  useHotkeys("left", goToPreviousMonth, [options.date, options.startDate]);
  useHotkeys("right", goToNextMonth, [options.date, options.endDate]);

  const formattedWellIds =
    options.wellIds
      ?.map((id) => {
        const parts = id.split("/");
        return parts.length > 1 ? parts.slice(1).join("/") : id;
      })
      .join(", ") ?? "";

  return (
    <NetworkMapOptionsRoot>
      <OptionItem>
        <Text>Well UWI</Text>
        <Text strong style={{ fontSize: "0.85em" }}>
          {formattedWellIds}
        </Text>
      </OptionItem>
      <OptionItem>
        <Text>Primary Path Only</Text>
        <Switch
          size="small"
          checked={options.showOnlyPrimaryPath}
          onChange={(checked) =>
            options.onChange?.({
              ...options,
              showOnlyPrimaryPath: checked
            })
          }
        />
      </OptionItem>
      <OptionItem>
        <Text>Month</Text>
        <MonthSelection>
          <IconButton
            disabled={!canGoToPreviousMonth()}
            onClick={goToPreviousMonth}
            path={mdiArrowLeftBold}
            size={0.65}
          />
          <Text style={{ margin: "0 4px", fontSize: "0.85em" }}>
            {options.date
              ? moment(options.date, "YYYY-MM-DD").format("MMM YYYY")
              : "Latest"}
          </Text>
          <IconButton
            disabled={!canGoToNextMonth()}
            onClick={goToNextMonth}
            path={mdiArrowRightBold}
            size={0.65}
          />
          <IconButton
            onClick={() =>
              options.onChange?.({
                ...options,
                date: undefined
              })
            }
            path={mdiRefresh}
            size={0.65}
          />
        </MonthSelection>
      </OptionItem>
      <OptionItem>
        <Text>Hide Connections LT</Text>
        <Input
          size="small"
          type="number"
          suffix="%"
          style={{ width: 80 }}
          value={options.hideConnectionsLessThanPercent ?? 0}
          onChange={(e) =>
            options.onChange?.({
              ...options,
              hideConnectionsLessThanPercent: Number(e.target.value)
            })
          }
        />
      </OptionItem>
      <OptionItem>
        <Text>Width</Text>
        <Slider
          range
          min={1}
          max={20}
          value={[options.width[0], options.width[1]] as [number, number]}
          onChange={(value) => options.onWidthChange?.(value as [number, number])}
          style={{ width: 120, margin: "0 8px" }}
        />
      </OptionItem>
    </NetworkMapOptionsRoot>
  );
}

// ── Styled Components ────────────────────────────────────────────────────────

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  .ant-typography {
    font-size: 0.85em;
  }
`;

const MonthSelection = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const NetworkMapOptionsRoot = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  pointer-events: auto;
  width: 100%;
`;

const ContextMenuContent = styled.div`
  display: flex;
  flex-direction: column;

  .ant-btn {
    text-align: left;
    justify-content: flex-start;
    padding: 4px 12px;
    height: auto;
  }

  .ant-divider {
    margin: 4px 0;
  }
`;

const Root = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;

  > * {
    pointer-events: auto;
  }
`;
